import React, { FC, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Image from "next/image";
import { ButtonType } from "components/atoms/Button/types";
import CustomJoyride from "components/atoms/Joyride";
import { Modal } from "components/atoms/Modal";
import SocialShare from "components/atoms/SocialShare";
import { useTutorial } from "components/organisms/TutorialProvider";
import { AppContext } from "lib/contexts";
import { ImageDTO } from "types";
import { shareModalTutorialSteps } from "utils/joyride/shareModalTutorialSteps";
import { Small, Span, SubTitle, Text } from "utils/styling/typography.styled";
import * as Styled from "./ShareModal.styled";
interface ShareModalProps {
  isOpen: boolean;
  image: ImageDTO | null;
  onClose: () => void;
}
const ShareModal: FC<ShareModalProps> = ({
  isOpen,
  image,
  onClose
}) => {
  const {
    state
  } = useContext(AppContext);
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (isOpen && tutorialState.controlsContainer.completed && !tutorialState.shareModal.completed && !tutorialState.shareModal.started) {
      startTutorial("shareModal");
    }
  }, [isOpen, tutorialState, startTutorial, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.shareModal.started && !tutorialState.shareModal.completed) {
      setRun(true);
    }
  }, [tutorialState]);
  const handleJoyrideCallback = (data: any) => {
    const {
      status,
      action,
      type
    } = data;
    if (["finished", "skipped"].includes(status)) {
      completeTutorial("shareModal");
      setRun(false);
      setStepIndex(0);
      onClose();
    } else if (type === "step:after") {
      if (action === "next") {
        setStepIndex(prev => prev + 1);
      } else if (action === "prev") {
        setStepIndex(prev => prev - 1);
      }
    }
  };
  if (!image) {
    return null;
  }
  const referralCode = state.referralCode;
  const shareUrl = `https://char-gen.com/images/${image.id}${referralCode ? `?referralCode=${referralCode}` : ""}`;
  const {
    theme,
    username,
    npcFieldsMap,
    requestType
  } = image.imageMetadata;
  const race = npcFieldsMap?.["Race"] || "";
  const characterClass = npcFieldsMap?.["Class"] || "";
  const occupation = npcFieldsMap?.["Occupation"] || "";
  const monster = npcFieldsMap?.["Monster"] || "";
  const itemName = npcFieldsMap?.["Item Name"] || "";
  const landscapeName = npcFieldsMap?.["Landscape Name"] || "";
  const itemType = npcFieldsMap?.["Item Type"] || "";
  let title = "";
  let description = "";
  switch (requestType) {
    case "LANDSCAPE":
      title = `${landscapeName} Landscape Art by ${username}`;
      description = `Theme: ${theme}`;
      break;
    case "ITEM":
      title = `${itemName} - ${itemType} Item Art by ${username}`;
      description = `Theme: ${theme}`;
      break;
    case "NPC":
      title = `NPC Art by ${username} - ${theme}`;
      description = [race, characterClass, occupation, theme].filter(Boolean).join(" ");
      break;
    case "MONSTER":
      title = `Monster Art by ${username} - ${theme}`;
      description = [monster, theme].filter(Boolean).join(" ");
      break;
    case "CHARACTER":
      title = `Character Art by ${username} - ${theme}`;
      description = [race, characterClass, occupation, theme].filter(Boolean).join(" ");
      break;
    default:
      title = `Art by ${username} - ${theme}`;
      description = theme;
  }
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link Copied to Clipboard!", {
      position: "bottom-center"
    });
  };
  return <>
            {run && <CustomJoyride steps={shareModalTutorialSteps} run={run} stepIndex={stepIndex} disableScrolling={false} callback={handleJoyrideCallback} />}
            <Modal open={isOpen} handleBackdropClick={onClose} disableBackdropClick data-sentry-element="Modal" data-sentry-source-file="index.tsx">
                <Styled.ModalContainer className="joyride-share-modal" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="index.tsx">Share this creation</SubTitle>
                    <Styled.ImageContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Image src={image.image} alt={title} width={500} height={500} data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                    </Styled.ImageContainer>
                    <Styled.Button variant={ButtonType.SECONDARY} onClick={copyToClipboard} className="joyride-copy-link-button" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Copy Link</Span>
                    </Styled.Button>
                    <Styled.Content data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.TextContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">{title}</Text>
                            <Small className="joyride-share-url" data-sentry-element="Small" data-sentry-source-file="index.tsx">
                                {shareUrl}
                            </Small>
                        </Styled.TextContainer>
                    </Styled.Content>
                    <SocialShare image={image} description={description} data-sentry-element="SocialShare" data-sentry-source-file="index.tsx" />
                    <Styled.HighlightText className="joyride-share-instructions" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        Share any image and tag CharGen to earn 10 free gold!
                        Use @chargen_ai on Instagram and TikTok.
                    </Styled.HighlightText>
                    <Styled.Button type="button" variant={ButtonType.PRIMARY} onClick={onClose} className="joyride-close-share-modal" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Close</Span>
                    </Styled.Button>
                </Styled.ModalContainer>
            </Modal>
        </>;
};
export default ShareModal;